import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Show, SignIn, SignUp, UserButton } from "@clerk/react";
import { Toaster } from "@/components/ui/sonner";
import ClerkAuthProvider from "@/components/auth/ClerkAuthProvider";
import Layout, { Logo } from "@/components/Layout";
import { useApi } from "@/lib/api";
import Dashboard from "@/pages/Dashboard";
import Clients from "@/pages/Clients";
import ClientNew from "@/pages/ClientNew";
import ClientDetail from "@/pages/ClientDetail";
import Transactions from "@/pages/Transactions";
import Transfers from "@/pages/Transfers";
import Statements from "@/pages/Statements";
import UsersPage from "@/pages/Users";
import Download from "@/pages/Download";
import Updates from "@/pages/Updates";

const AuthShell = ({ children }) => (
  <div className="grid min-h-screen lg:grid-cols-2">
    <div className="relative hidden flex-col justify-between overflow-hidden bg-slate-900 p-12 text-white lg:flex">
      <div className="absolute -right-24 -top-24 h-96 w-96 rounded-full bg-teal-500/20 blur-3xl" />
      <div className="inline-flex w-fit rounded-2xl bg-white px-5 py-3"><Logo className="h-10" /></div>
      <div className="relative space-y-4">
        <h1 className="text-5xl font-bold leading-tight tracking-tight">Cuentas, CLABEs y SPEI<br /><span className="text-teal-300">en un solo lugar.</span></h1>
        <p className="max-w-md text-slate-300">Back-office de e-Plata para alta de clientes PM y PF, expedientes KYC, movimientos y estados de cuenta operados vía STP.</p>
      </div>
      <p className="text-xs text-slate-500">ePlata México, S.A. de C.V. · Institución de Fondos de Pago Electrónico</p>
    </div>
    <div className="flex flex-col items-center justify-center gap-8 bg-white p-6">
      <div className="lg:hidden"><Logo className="h-10" /></div>
      {children}
    </div>
  </div>
);

const NoAccess = () => (
  <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-slate-50 p-6 text-center" data-testid="no-access-screen">
    <Logo className="h-10" />
    <div className="max-w-md space-y-2">
      <h1 className="text-2xl font-semibold text-slate-900">Acceso pendiente de aprobación</h1>
      <p className="text-sm text-slate-500">Tu cuenta aún no tiene permisos de administrador. Pide a un administrador de e-Plata que te otorgue acceso.</p>
    </div>
    <UserButton />
  </div>
);

const AdminGate = () => {
  const api = useApi();
  const [me, setMe] = useState(null);
  useEffect(() => {
    api.get("/me").then((r) => setMe(r.data)).catch(() => setMe({ role: "error" }));
  }, [api]);
  if (!me) return <div className="flex min-h-screen items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-2 border-teal-600 border-t-transparent" /></div>;
  return me.role === "admin" ? <Layout /> : <NoAccess />;
};

const Protected = () => (
  <>
    <Show when="signed-in"><AdminGate /></Show>
    <Show when="signed-out"><Navigate to="/sign-in" replace /></Show>
  </>
);

function App() {
  return (
    <BrowserRouter>
      <ClerkAuthProvider>
        <Routes>
          <Route path="/descarga/:token" element={<Download />} />
          <Route path="/sign-in/*" element={<AuthShell><SignIn routing="path" path="/sign-in" signUpUrl="/sign-up" withSignUp /></AuthShell>} />
          <Route path="/sign-up/*" element={<AuthShell><SignUp routing="path" path="/sign-up" signInUrl="/sign-in" /></AuthShell>} />
          <Route element={<Protected />}>
            <Route path="/" element={<Dashboard />} />
            <Route path="/clientes" element={<Clients />} />
            <Route path="/clientes/nuevo" element={<ClientNew />} />
            <Route path="/clientes/:id" element={<ClientDetail />} />
            <Route path="/clientes/:id/editar" element={<ClientNew />} />
            <Route path="/transacciones" element={<Transactions />} />
            <Route path="/transferencias" element={<Transfers />} />
            <Route path="/estados-cuenta" element={<Statements />} />
            <Route path="/usuarios" element={<UsersPage />} />
            <Route path="/actualizar" element={<Updates />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </ClerkAuthProvider>
      <Toaster richColors position="top-right" />
    </BrowserRouter>
  );
}

export default App;
